import { useCallback, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { queryKeys } from '@/hooks/api/queryKeys';
import { useFeatureFlags } from '@/hooks/useFeatureFlags';
import { flagsmithFeatureFlags } from '@/lib/flagsmith';
import { fetchDocsManifest, type ManifestDoc } from './docsManifest';

/** Every flag the app knows. The docs may gate any of them (inline or per doc). */
const KNOWN_FLAGS: string[] = Object.values(flagsmithFeatureFlags);
const NO_FLAGS: string[] = [];

export interface DocsManifestView {
  /** Docs the user may see, in navigation order: a doc behind a flag that is off is left out. */
  docs: ManifestDoc[];
  /** The doc for a screen, if it has one and the user may see it. */
  docForScreen: (screenKey: string | null) => ManifestDoc | null;
  /**
   * The app's flag values, for the docs' inline `<FeatureFlag>` sections.
   * Null until they have loaded: while loading, every flag reads as off.
   */
  flags: Record<string, boolean> | null;
  isLoading: boolean;
  isError: boolean;
}

/**
 * Loads `/help/manifest.json` and the flags it needs (only while `enabled`,
 * i.e. the in-app help is on) and filters the docs by the user's feature
 * flags. Flags fail closed: while they load, every flagged doc stays hidden.
 */
export function useDocsManifest(enabled: boolean): DocsManifestView {
  const query = useQuery({
    queryKey: queryKeys.help.manifest,
    queryFn: ({ signal }) => fetchDocsManifest(signal),
    enabled,
    // Changes only with a deploy.
    staleTime: 30 * 60 * 1000,
  });
  const manifest = query.data;

  const docFlags = useMemo(
    () =>
      (manifest?.docs ?? [])
        .map(doc => doc.featureFlag)
        .filter((flag): flag is string => !!flag),
    [manifest]
  );
  const flagKeys = useMemo(
    () => [...new Set([...KNOWN_FLAGS, ...docFlags])],
    [docFlags]
  );
  // Nothing to evaluate while the help is off: the button is on every screen.
  const { flags: liveFlags, isLoading: flagsLoading } = useFeatureFlags(
    enabled ? flagKeys : NO_FLAGS
  );
  // useFeatureFlags returns a new object every render; keep one per value so
  // the docs frame is sent flags only when they change.
  const flagsKey = JSON.stringify(liveFlags);
  const flags = useMemo<Record<string, boolean>>(
    () => JSON.parse(flagsKey),
    [flagsKey]
  );

  const docs = useMemo(
    () =>
      (manifest?.docs ?? []).filter(
        doc =>
          !doc.featureFlag || (!flagsLoading && flags[doc.featureFlag] === true)
      ),
    [manifest, flags, flagsLoading]
  );

  const docForScreen = useCallback(
    (screenKey: string | null): ManifestDoc | null =>
      screenKey
        ? (docs.find(doc => doc.screenKeys.includes(screenKey)) ?? null)
        : null,
    [docs]
  );

  return {
    docs,
    docForScreen,
    flags: enabled && !flagsLoading ? flags : null,
    isLoading: enabled && (query.isLoading || flagsLoading),
    isError: query.isError,
  };
}
